import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  Permission,
  StudentService,
  StudentServiceAdditionalEntry,
  StudentServiceEditResource,
  StudentServiceViewResource,
} from "generated/openApi";
import { useRouter } from "hooks";
import produce from "immer";
import "moment-timezone";
import {
  ProviderCreateContext,
  providerCreatePageRoute,
} from "pages/providers/providers/ProviderCreatePage/ProviderCreatePage";
import { CollegeContext, collegePageRoute } from "pages/system/utility/colleges/CollegePage/CollegePage";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { StudentServicePageBody } from "./StudentServicePageBody/StudentServicePageBody";
import { StudentServicePageHeader } from "./StudentServicePageHeader";
import {
  StudentServiceIndexListContext,
  studentServiceIndexListPageRoute,
} from "pages/students/student-service-indexes/StudentServiceIndexListPage/StudentServiceIndexListPage";

const routeName = "studentService";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  studentId?: number = 0;
  hideStudent?: boolean = false;
}

export const studentServicePageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Service",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

const ROOT_PERMISSION: Permission = Permission.StudentService;

export const StudentServicePage = () => {
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const { matchTemplatePath } = useRouter();

  const [hideStudent] = useState<boolean>(optionalParams.hideStudent || false);
  const [studentId] = useState<number | undefined>(optionalParams.studentId);

  const [service, setService] = useState<StudentService>({} as StudentService);
  const [initService, setInitService] = useState<StudentService>(service);
  const [additionalDates, setAdditionalDates] = useState<Date[]>([]);
  const [additionalEntries, setAdditionalEntries] = useState<StudentServiceAdditionalEntry[]>([]);

  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({
    trackedItems: [service, additionalDates, additionalEntries],
  });
  const [viewResource, setViewResource] = useState<StudentServiceViewResource>({});
  const [editResource, setEditResource] = useState<StudentServiceEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);
    fetchViewResource();
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(
      `../${studentServicePageRoute.getPath({
        id: id,
        crudMode: crudMode,
        screenType: params.screenType,
      })}`
    );

  const fetch = (id: number) => {
    ApiService.studentServices.get(id).then((response) => {
      if (!id && studentId) response.studentId = studentId;

      setService(response);
      setInitService(response);
      resetUnsavedChanges([response, additionalDates, additionalEntries]);
    });
  };

  const fetchViewResource = () => {
    ApiService.studentServices.getViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.studentServices.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const handleProviderCreate = (id: number) => {
    fetchEditResource();
    ApiService.providers.get(id).then((response) => {
      setService(
        produce(service, (draft) => {
          draft.providers = draft.providers || [];
          draft.providers.push(response);
        })
      );
    });
  };

  const handleCollegeCreate = (id: number) => {
    fetchEditResource();
    ApiService.colleges.get(id).then((response) => {
      setService(
        produce(service, (draft) => {
          draft.colleges = draft.colleges || [];
          draft.colleges.push(response);
        })
      );
    });
  };

  const handleServiceIndexSelect = (id: number) => {
    ApiService.studentServiceIndexes.get(id).then((response) => {
      setService(
        produce(service, (draft) => {
          draft.serviceTypeId = response.serviceTypeId!;
          draft.serviceSubTypeId = response.serviceSubTypeId!;
          draft.name = response.name;
          draft.providers = [];

          if (response.providerId) {
            const provider = editResource.providers?.find((p) => p.id === response.providerId);
            if (provider) draft.providers = [provider];
          }
        })
      );
    });
  };

  const pageHeader = (
    <StudentServicePageHeader
      rootPermission={ROOT_PERMISSION}
      handle={studentServicePageRoute.handle}
      crudMode={params.crudMode}
      theme={params.screenType == "dialog" ? "Dark" : "Standard"}
      reroute={reroute}
      fetch={fetch}
      service={service}
      setService={setService}
      initService={initService}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
      resetUnsavedChanges={resetUnsavedChanges}
      additionalEntries={additionalEntries}
      setAdditionalEntries={setAdditionalEntries}
      additionalDates={additionalDates}
      setAdditionalDates={setAdditionalDates}
      hideStudent={hideStudent}
    />
  );

  const pageBody = (
    <StudentServicePageBody
      rootPermission={ROOT_PERMISSION}
      crudMode={params.crudMode}
      service={service}
      setService={setService}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
      hideStudent={hideStudent}
      onEdit={() => reroute(params.id, "edit")}
      additionalEntries={additionalEntries}
      setAdditionalEntries={setAdditionalEntries}
      additionalDates={additionalDates}
      setAdditionalDates={setAdditionalDates}
    />
  );

  return (
    <>
      {params.screenType === "standard" ? (
        <Page isForm header={pageHeader}>
          <Card>{pageBody}</Card>
        </Page>
      ) : (
        <Dialog
          isForm
          open={true}
          onClose={() => handleClose(navigate, unsavedChanges)}
          maxWidth="lg"
          header={pageHeader}
          content={pageBody}
        />
      )}

      <ProviderCreateContext.Provider
        value={{
          onCreate: handleProviderCreate,
        }}
      >
        {matchTemplatePath(providerCreatePageRoute.templatePath) && <Outlet />}
      </ProviderCreateContext.Provider>

      <CollegeContext.Provider
        value={{
          onCreate: handleCollegeCreate,
        }}
      >
        {matchTemplatePath(collegePageRoute.templatePath) && <Outlet />}
      </CollegeContext.Provider>

      <StudentServiceIndexListContext.Provider
        value={{
          onSelectIndex: handleServiceIndexSelect,
        }}
      >
        {matchTemplatePath(studentServiceIndexListPageRoute.templatePath) && <Outlet />}
      </StudentServiceIndexListContext.Provider>
    </>
  );
};
